import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Última actualización: Marzo 2026

En Paz en la Bandeja, respetamos tu privacidad y nos comprometemos a proteger tus datos personales.

**Datos que recopilamos**
Al realizar tu compra, recopilamos únicamente tu dirección de correo electrónico para enviarte el producto digital adquirido.

**Uso de la información**
Tu email se utiliza exclusivamente para:
• Enviarte el PDF del protocolo tras la compra.
• Comunicarte actualizaciones importantes del producto (solo si lo autorizás).

**Compartir datos**
No vendemos, alquilamos ni compartimos tu información personal con terceros, salvo que sea necesario para procesar tu pago a través de nuestra plataforma de cobro segura.

**Cookies**
Nuestro sitio puede utilizar cookies básicas para mejorar tu experiencia de navegación. No utilizamos cookies de rastreo publicitario.

**Tus derechos**
Podés solicitar la eliminación de tus datos en cualquier momento escribiéndonos por email.

**Seguridad**
Implementamos medidas de seguridad estándar de la industria para proteger tu información personal.
`.trim();
const termsContent = `
Última actualización: Marzo 2026

Al adquirir el producto "Paz en la Bandeja", aceptás los siguientes términos y condiciones:

**Producto digital**
"Paz en la Bandeja" es un producto digital (PDF) de descarga inmediata. Una vez realizada la compra, recibirás el archivo en tu correo electrónico.

**Uso personal**
El contenido es para uso personal y no puede ser redistribuido, revendido ni compartido sin autorización expresa.

**Propiedad intelectual**
Todo el contenido del protocolo, incluyendo textos, gráficos y metodologías, es propiedad intelectual de Paz en la Bandeja. Queda prohibida su reproducción total o parcial.

**Resultados**
Los resultados pueden variar según cada caso particular. El protocolo ofrece herramientas basadas en etología felina, pero no garantiza resultados específicos ya que cada gato y hogar es diferente.

**Aviso veterinario**
Este producto no reemplaza la consulta veterinaria. Si tu gato presenta síntomas de enfermedad urinaria (sangre en la orina, esfuerzo al orinar, llanto), consultá a un veterinario de inmediato.

**Pagos**
El precio publicado es en pesos argentinos (ARS). El pago es único y no recurrente.

**Reembolsos**
Dado que se trata de un producto digital de descarga inmediata, no se aceptan reembolsos una vez entregado el archivo.
`.trim();
function formatContent(text: string) {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**')) {
      return <h3 key={i} className="font-['Playfair_Display'] text-lg font-semibold text-[#1C1C1C] mt-6 mb-2" data-id="element-24">
          {line.replace(/\*\*/g, '')}
        </h3>;
    }
    if (line.startsWith('•')) {
      return <p key={i} className="text-[#1C1C1C]/80 leading-relaxed pl-4" data-id="element-25">
          {line}
        </p>;
    }
    if (line.trim() === '') {
      return <div key={i} className="h-2" data-id="element-26" />;
    }
    return <p key={i} className="text-[#1C1C1C]/80 leading-relaxed" data-id="element-27">
        {line}
      </p>;
  });
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#1C1C1C] py-8 px-6" data-id="element-28">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-29">
          <button onClick={() => setActiveModal('privacy')} className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer" data-id="element-30">
            Política de Privacidad
          </button>
          <span className="hidden sm:inline text-white/30" data-id="element-31">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer" data-id="element-32">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-white/40 text-xs text-center mt-4" data-id="element-33">
          © 2026 Paz en la Bandeja. Todos los derechos reservados.
        </p>
      </footer>

      <AnimatePresence data-id="element-34">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-35">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-36">
              <div className="flex items-center justify-between p-6 border-b border-[#1C1C1C]/10" data-id="element-37">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1C1C]" data-id="element-38">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 hover:bg-[#E8F0E8] rounded-lg transition-colors cursor-pointer" data-id="element-39">
                  <XIcon className="w-5 h-5 text-[#1C1C1C]" data-id="element-40" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-41">
                {formatContent(activeModal === 'privacy' ? privacyContent : termsContent)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}